import { jsx as _jsx } from "react/jsx-runtime";
import { setAnalyticsClinic } from '@/lib/analytics';
import { getAllClinics, getCurrentUser } from '@/lib/api/admin';
import { isSuperAdminFromToken } from '@/lib/api/client';
import { syncSentryUser } from '@/lib/sentry';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useAuth } from './use-auth';
export function hasEffectivePermission(clinic, permission) {
    return clinic.isSuperAdminAccess || clinic.permissions.includes(permission);
}
const CurrentUserContext = createContext({
    currentUser: null,
    isSuperAdmin: false,
    canManageUsers: false,
    canViewStudies: false,
    canSignReport: false,
    canAddAddendum: false,
    canUnfinalizeReport: false,
    canImportData: false,
    isTeleconsultationOnly: false,
    isLinkOnly: false,
    hasLinkClinic: false,
    isLoading: true,
    isError: false,
    isFetching: false,
});
export function CurrentUserProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const { data, isLoading, isError, isFetching } = useQuery({
        queryKey: ['currentUser'],
        queryFn: getCurrentUser,
        staleTime: 5 * 60 * 1000, // 5 minutes
        enabled: isAuthenticated,
    });
    const currentUser = data ?? null;
    // Keep Sentry's user context in step with the session so every captured
    // error answers "who hit this" (and clears again on sign-out).
    useEffect(() => {
        syncSentryUser(currentUser);
    }, [currentUser]);
    // Give analytics a clinic to stamp events with as soon as one is unambiguous — the vast
    // majority of users belong to exactly one clinic, and without this their worklist and
    // navigation events carry no clinic at all, so "is this slow for one clinic or for
    // everyone?" can only be answered for people who opened a study. Multi-clinic users are
    // left alone here (there is no right answer before they act); the viewer sets the clinic
    // of whatever study they open. Cleared on sign-out.
    useEffect(() => {
        const memberships = currentUser?.clinicRoles ?? [];
        if (!currentUser)
            setAnalyticsClinic(null);
        else if (memberships.length === 1)
            setAnalyticsClinic(memberships[0].clinicId);
    }, [currentUser]);
    // Super-admin is a global identity flag read from the access token's
    // is_super_admin claim, not a 'Super Admin' role-name match — a
    // membership-less super admin has an empty clinicRoles. /users/me echoes the
    // same claim back, but sourcing the flag from there ties it to a call that
    // 404s in a region holding no profile row for the identity, which silently
    // demotes the super admin and hides the region switcher they need to leave
    // that region. The token is the single source of truth (isSuperAdminFromToken).
    const isSuperAdmin = isAuthenticated && isSuperAdminFromToken();
    // Capability booleans: super admins bypass per-permission grants on every
    // clinic they have any access to. Regular users need an explicit
    // user_clinic + role_permission row granting the permission. Consumers
    // wanting the *list* of clinics where a capability applies should use
    // useEffectiveClinics(permission) instead.
    const canManageUsers = isSuperAdmin ||
        (currentUser?.clinicRoles.some((cr) => cr.permissions.includes('manage_users')) ?? false);
    const canViewStudies = isSuperAdmin ||
        (currentUser?.clinicRoles.some((cr) => cr.permissions.includes('view_studies')) ?? false);
    // A membership-less super admin has an empty clinicRoles, which would
    // otherwise look identical to a teleconsultation-only user. The super-admin
    // claim wins: they manage all data and must keep the Studies surface (nav,
    // import, and the Home redirect to the worklist instead of /teleconsultation).
    const isTeleconsultationOnly = !!currentUser && currentUser.clinicRoles.length === 0 && !isSuperAdmin;
    // A NewLumen Link (free tier) practice member: every clinic membership is a
    // tier='link' clinic. They land in the Link shell (/link/app), never the
    // full-PACS worklist. Super admins are never link-only — they keep the full
    // surface for support access. Absent tier fields (backend not shipped yet)
    // mean 'standard', so existing users are unaffected.
    const isLinkOnly = !!currentUser &&
        !isSuperAdmin &&
        currentUser.clinicRoles.length > 0 &&
        currentUser.clinicRoles.every((cr) => cr.tier === 'link');
    // Looser than isLinkOnly: true whenever at least one membership is a
    // tier='link' clinic, even if the user also belongs to standard-tier
    // clinics. This mirrors LinkController.ResolveLinkClinicAsync, which picks
    // the caller's link-tier clinic out of all their memberships rather than
    // requiring every membership to be link-tier. Gates the Link shell
    // (LinkAppShell) so a user with no link-tier clinic at all never lands on
    // a surface whose backend calls are guaranteed to 403.
    const hasLinkClinic = !!currentUser && currentUser.clinicRoles.some((cr) => cr.tier === 'link');
    const canSignReport = isSuperAdmin ||
        currentUser?.clinicRoles.some((cr) => cr.permissions.includes('sign_report')) ||
        isTeleconsultationOnly;
    const canAddAddendum = isSuperAdmin ||
        currentUser?.clinicRoles.some((cr) => cr.permissions.includes('addend_report')) ||
        isTeleconsultationOnly;
    const canUnfinalizeReport = canSignReport || isSuperAdmin;
    const canImportData = isSuperAdmin ||
        (currentUser?.clinicRoles.some((cr) => cr.permissions.includes('import_data')) ?? false);
    return (_jsx(CurrentUserContext.Provider, { value: {
            currentUser,
            isSuperAdmin,
            canManageUsers,
            canViewStudies,
            canSignReport,
            canAddAddendum,
            canUnfinalizeReport,
            canImportData,
            isTeleconsultationOnly,
            isLinkOnly,
            hasLinkClinic,
            isLoading,
            isError,
            isFetching,
        }, children: children }));
}
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentUser() {
    return useContext(CurrentUserContext);
}
// Returns the list of clinics the current user can act on, with super admins
// fanned out to every active clinic via GET /clinic. If `permission` is
// supplied, regular users get only the memberships granting that permission;
// super admins always get the full list (their access bypasses per-permission
// gates — backend authorization endpoints already encode this).
//
// Consumers should prefer this hook over reading currentUser.clinicRoles
// directly when they need a clinic-picker, because /users/me only returns
// user_clinic rows and would otherwise hide all-but-home-clinic access for
// super admins.
// eslint-disable-next-line react-refresh/only-export-components
export function useEffectiveClinics(permission) {
    const { currentUser, isSuperAdmin, isLoading: isUserLoading } = useCurrentUser();
    const { data: allClinicsData, isLoading: isAllClinicsLoading, isError: isAllClinicsError, refetch: refetchAllClinics, } = useQuery({
        queryKey: ['clinics', 'all'],
        queryFn: getAllClinics,
        staleTime: 5 * 60 * 1000,
        enabled: isSuperAdmin,
    });
    const clinics = useMemo(() => {
        if (isSuperAdmin) {
            // Super admin synthetic entries — permissions array is empty; capability
            // checks must go through hasEffectivePermission, which short-circuits on
            // isSuperAdminAccess.
            return (allClinicsData ?? []).map((c) => ({
                clinicId: c.id,
                clinicCode: c.code,
                clinicName: c.name,
                clinicLogoUrl: c.logoUrl,
                companyName: c.companyName,
                footerText: c.footerText,
                permissions: [],
                isSuperAdminAccess: true,
                ssoConfigured: c.ssoConfigured,
            }));
        }
        const roles = currentUser?.clinicRoles ?? [];
        const filtered = permission ? roles.filter((cr) => cr.permissions.includes(permission)) : roles;
        return filtered.map((cr) => ({
            clinicId: cr.clinicId,
            clinicCode: cr.clinicCode,
            clinicName: cr.clinicName,
            clinicLogoUrl: cr.clinicLogoUrl,
            companyName: cr.companyName ?? null,
            footerText: cr.footerText ?? null,
            permissions: cr.permissions,
            isSuperAdminAccess: false,
            ssoConfigured: cr.ssoConfigured ?? false,
        }));
    }, [isSuperAdmin, allClinicsData, currentUser, permission]);
    return {
        clinics,
        isLoading: isUserLoading || (isSuperAdmin && isAllClinicsLoading),
        isError: isSuperAdmin && isAllClinicsError,
        refetch: () => {
            void refetchAllClinics();
        },
    };
}
